import type { ChatMessage, FileRef, MessageContent, UserContent } from '@oneapp/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, iconFor, Mark } from '../components/icons';
import { BotRow, Divider } from '../components/ui';
import { fileLabel, sizeLabel } from '../imports/api';
import { type BotHandlers, BotMessage } from './BotMessage';
import { dividerLabel, localeFor, needsDivider } from './dates';
import { type ChatState, effectiveStatus, type Outgoing } from './state';

export interface ListHandlers extends BotHandlers {
  retry(clientId: string): void;
  remove(clientId: string): void;
}

/**
 * The conversation: day dividers, the bot's messages grouped beside its mark, the person's own
 * messages (with "Not sent — Retry" when one didn't go), and the typing indicator.
 */
export function MessageList({ state, handlers, now }: { state: ChatState; handlers: ListHandlers; now: Date }) {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  const items: ReactNode[] = [];
  let group: ChatMessage[] = [];
  let previous: Date | null = null;

  const flush = () => {
    const first = group[0];
    if (!first) return;
    items.push(
      <BotRow key={first.id}>
        {group.map((message) => {
          const { status, selected } = effectiveStatus(state, message);
          return (
            <BotMessage
              key={message.id}
              message={message}
              open={status === 'active' && !state.ended}
              selected={selected}
              handlers={handlers}
            />
          );
        })}
      </BotRow>,
    );
    group = [];
  };

  for (const message of state.messages) {
    const at = new Date(message.createdAt);
    if (needsDivider(previous, at)) {
      flush();
      items.push(<Divider key={`day-${message.id}`}>{dividerLabel(at, previous === null, now, t, locale)}</Divider>);
    }
    previous = at;
    if (message.from === 'bot') {
      group.push(message);
      continue;
    }
    flush();
    items.push(
      message.from === 'user' ? (
        <UserMessage key={message.id} content={message.content} />
      ) : (
        <SystemNote key={message.id} content={message.content} />
      ),
    );
  }
  flush();

  for (const outgoing of state.outgoing) {
    items.push(
      <UserMessage key={outgoing.clientId} content={outgoing.content} outgoing={outgoing} handlers={handlers} />,
    );
  }
  return <>{items}</>;
}

function UserMessage({
  content,
  outgoing,
  handlers,
}: {
  content: MessageContent;
  outgoing?: Outgoing;
  handlers?: ListHandlers;
}) {
  const { t } = useTranslation();
  const body = userText(content as UserContent);
  if (body === null) return null;
  const failed = outgoing?.state === 'failed';
  const sending = outgoing?.state === 'sending';
  const icon = content.type === 'user_submit' ? iconFor(content.icon) : null;
  return (
    <div className="user-msg">
      <p
        className={`bubble bubble--user${content.type === 'user_files' ? ' bubble--files' : ''}${sending ? ' bubble--sending' : ''}${failed ? ' bubble--failed' : ''}`}
      >
        {icon ? <Icon name={icon} size={16} /> : null}
        {content.type === 'user_files' && content.files.length > 1 && content.files.every(isPicture) ? (
          // Photos sent: how many, and how much ("7 photos · 2.1 MB").
          <span className="file-tile">
            <Icon name="camera" size={20} />
            <span className="file-tile__text">
              <span className="file-tile__name">{t('chat.photos', { count: content.files.length })}</span>
              <span className="file-tile__meta">{sizeLabel(content.files.reduce((sum, f) => sum + f.size, 0))}</span>
            </span>
          </span>
        ) : content.type === 'user_files' ? (
          content.files.map((file) => (
            // A file sent: its name, and its kind and size ("CSV · 2 KB").
            <span key={file.id} className="file-tile">
              <Icon name={isPicture(file) ? 'camera' : 'file-text'} size={20} />
              <span className="file-tile__text">
                <span className="file-tile__name">{file.name}</span>
                <span className="file-tile__meta">{fileLabel(file.name, file.size)}</span>
              </span>
            </span>
          ))
        ) : (
          body
        )}
        {sending ? (
          <span className="bubble__pending">
            <Icon name="clock" size={13} />
            <span className="visually-hidden">{t('chat.sending')}</span>
          </span>
        ) : null}
      </p>
      {failed && outgoing && handlers ? (
        <p className="user-msg__failed" role="alert">
          <Icon name="alert-triangle" size={15} />
          <span>
            {t('chat.notSent')} {t(`chat.failed.${outgoing.error ?? 'server'}`)}
          </span>
          <button type="button" className="linkbtn" onClick={() => handlers.retry(outgoing.clientId)}>
            {t('chat.retry')}
          </button>
          <button type="button" className="linkbtn linkbtn--muted" onClick={() => handlers.remove(outgoing.clientId)}>
            {t('chat.remove')}
          </button>
        </p>
      ) : null}
    </div>
  );
}

/** What the person's own message says, as plain text (it's never read as markup). */
function userText(content: UserContent): string | null {
  switch (content.type) {
    case 'user_text':
      return content.text;
    case 'user_choice':
      return content.label;
    case 'user_submit':
      return content.summary;
    case 'user_files':
      return content.files.map((f) => f.name).join(', ');
    default:
      return null;
  }
}

function SystemNote({ content }: { content: MessageContent }) {
  if (content.type !== 'system') return null;
  return <p className="system-note">{content.text}</p>;
}

export function Typing({ progress }: { progress?: string | null }) {
  const { t } = useTranslation();
  return (
    <div className="bot-row typing">
      <Mark size={28} />
      <span className="typing__dots" aria-hidden="true" hidden={!!progress}>
        <i />
        <i />
        <i />
      </span>
      <span className={progress ? 'typing__progress' : 'visually-hidden'} role="status">
        {progress ?? t('chat.typing')}
      </span>
    </div>
  );
}

const isPicture = (file: FileRef) => file.mime.startsWith('image/');
